import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Enums,
  HuntCoverage,
  MitreHeatMapCoverage,
} from '@cyborg-monorepo/hunter/common';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
} from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { DashboardUrlService } from '../../hunt/overview/dashboard/dashboard-url-service';

export interface HuntPriorities {
  results: {
    priority: Enums.PriorityEnum;
    count: number;
    color?: string;
  }[];
  count: number;
}

export interface HuntStatuses {
  results: {
    status: Enums.StatusEnum;
    count: number;
    color?: string;
  }[];
  count: number;
}

export interface HuntOutcomes {
  results: {
    outcome: Enums.OutcomeEnum | string;
    count: number;
    color?: string;
  }[];
  count: number;
}

export interface HuntOutcomesRange {
  results: {
    outcome: Enums.OutcomeEnum;
    counts: number[];
  }[];
}

export interface CompletedHuntsRange {
  results: number[];
}

export interface AssignedHunts {
  results: {
    id: string;
    firstname: string;
    lastname: string;
    fullname?: string;
    count: number;
    huntPackageCount?: number;
  }[];
}

@Injectable({
  providedIn: 'root',
})
export class HuntMetricsService {
  constructor(
    private httpClient: HttpClient,
    private dashboardUrlService: DashboardUrlService,
  ) {}

  private apiUrl: string = environment.api + '/v2';

  public getMyAssignedHunts(
    filters?: CompositeFilterDescriptor,
  ): Observable<AssignedHunts> {
    if (filters && filters.filters) {
      return this.httpClient.get<AssignedHunts>(
        `${this.apiUrl}/hunt-metrics/my-hunts`,
        {
          params: this.dashboardUrlService.stateToHttpParams(
            filters.filters as FilterDescriptor[],
          ),
        },
      );
    } else {
      return this.httpClient.get<AssignedHunts>(
        `${this.apiUrl}/hunt-metrics/my-hunts`,
      );
    }
  }

  public getHuntsByAssignee(
    filters?: CompositeFilterDescriptor,
  ): Observable<AssignedHunts> {
    if (filters && filters.filters) {
      return this.httpClient.get<AssignedHunts>(
        `${this.apiUrl}/hunt-metrics/assigned-hunts`,
        {
          params: this.dashboardUrlService.stateToHttpParams(
            filters.filters as FilterDescriptor[],
          ),
        },
      );
    } else {
      return this.httpClient.get<AssignedHunts>(
        `${this.apiUrl}/hunt-metrics/assigned-hunts`,
      );
    }
  }

  public getHuntActorCoverageById(
    id: string,
  ): Observable<{ results: HuntCoverage[] }> {
    return this.httpClient.get<{ results: HuntCoverage[] }>(
      `${this.apiUrl}/hunt-metrics/${id}/actor-coverage`,
    );
  }

  public getHuntThreatCoverageById(
    id: string,
  ): Observable<{ results: HuntCoverage[] }> {
    return this.httpClient.get<{ results: HuntCoverage[] }>(
      `${this.apiUrl}/hunt-metrics/${id}/threat-coverage`,
    );
  }

  public getHuntMITRECoverageById(
    id: string,
  ): Observable<{ results: HuntCoverage[] }> {
    return this.httpClient.get<{ results: HuntCoverage[] }>(
      `${this.apiUrl}/hunt-metrics/${id}/mitre-coverage`,
    );
  }

  public getHuntActorCoverage(
    filters?: CompositeFilterDescriptor,
  ): Observable<{ results: HuntCoverage[] }> {
    if (filters && filters.filters) {
      return this.httpClient.get<{ results: HuntCoverage[] }>(
        `${this.apiUrl}/hunt-metrics/actor-coverage`,
        {
          params: this.dashboardUrlService.stateToHttpParams(
            filters.filters as FilterDescriptor[],
          ),
        },
      );
    } else {
      return this.httpClient.get<{ results: HuntCoverage[] }>(
        `${this.apiUrl}/hunt-metrics/actor-coverage`,
      );
    }
  }

  public getHuntThreatCoverage(
    filters?: CompositeFilterDescriptor,
  ): Observable<{ results: HuntCoverage[] }> {
    if (filters && filters.filters) {
      return this.httpClient.get<{ results: HuntCoverage[] }>(
        `${this.apiUrl}/hunt-metrics/threat-coverage`,
        {
          params: this.dashboardUrlService.stateToHttpParams(
            filters.filters as FilterDescriptor[],
          ),
        },
      );
    } else {
      return this.httpClient.get<{ results: HuntCoverage[] }>(
        `${this.apiUrl}/hunt-metrics/threat-coverage`,
      );
    }
  }

  public getHuntMITRECoverage(
    filters?: CompositeFilterDescriptor,
  ): Observable<{ results: HuntCoverage[] }> {
    if (filters && filters.filters) {
      return this.httpClient.get<{ results: HuntCoverage[] }>(
        `${this.apiUrl}/hunt-metrics/mitre-coverage`,
        {
          params: this.dashboardUrlService.stateToHttpParams(
            filters.filters as FilterDescriptor[],
          ),
        },
      );
    } else {
      return this.httpClient.get<{ results: HuntCoverage[] }>(
        `${this.apiUrl}/hunt-metrics/mitre-coverage`,
      );
    }
  }

  public getHuntMITREHeatmapCoverage(
    filters?: CompositeFilterDescriptor,
  ): Observable<{ results: Map<string, MitreHeatMapCoverage> }> {
    if (filters && filters.filters) {
      return this.httpClient.get<{
        results: Map<string, MitreHeatMapCoverage>;
      }>(`${this.apiUrl}/hunt-metrics/mitre-heatmap-coverage`, {
        params: this.dashboardUrlService.stateToHttpParams(
          filters.filters as FilterDescriptor[],
        ),
      });
    } else {
      return this.httpClient.get<{
        results: Map<string, MitreHeatMapCoverage>;
      }>(`${this.apiUrl}/hunt-metrics/mitre-heatmap-coverage`);
    }
  }

  public getDashboardHuntsByOutcome(
    filters?: CompositeFilterDescriptor,
  ): Observable<HuntOutcomes> {
    if (filters && filters.filters) {
      return this.httpClient.get<HuntOutcomes>(
        `${this.apiUrl}/hunt-metrics/hunt-outcomes`,
        {
          params: this.dashboardUrlService.stateToHttpParams(
            filters.filters as FilterDescriptor[],
          ),
        },
      );
    } else {
      return this.httpClient.get<HuntOutcomes>(
        `${this.apiUrl}/hunt-metrics/hunt-outcomes`,
      );
    }
  }

  public getDashboardHuntsByStatus(
    filters?: CompositeFilterDescriptor,
  ): Observable<HuntStatuses> {
    if (filters && filters.filters) {
      return this.httpClient.get<HuntStatuses>(
        `${this.apiUrl}/hunt-metrics/hunt-statuses`,
        {
          params: this.dashboardUrlService.stateToHttpParams(
            filters.filters as FilterDescriptor[],
          ),
        },
      );
    } else {
      return this.httpClient.get<HuntStatuses>(
        `${this.apiUrl}/hunt-metrics/hunt-statuses`,
      );
    }
  }

  public getDashboardHuntsByPriority(
    filters?: CompositeFilterDescriptor,
  ): Observable<HuntPriorities> {
    if (filters && filters.filters) {
      return this.httpClient.get<HuntPriorities>(
        `${this.apiUrl}/hunt-metrics/hunt-priorities`,
        {
          params: this.dashboardUrlService.stateToHttpParams(
            filters.filters as FilterDescriptor[],
          ),
        },
      );
    } else {
      return this.httpClient.get<HuntPriorities>(
        `${this.apiUrl}/hunt-metrics/hunt-priorities`,
      );
    }
  }

  public getDashboardHuntPackageCount(
    filters?: CompositeFilterDescriptor,
  ): Observable<{ count: number }> {
    if (filters && filters.filters) {
      return this.httpClient.get<{ count: number }>(
        `${this.apiUrl}/hunt-metrics/hunt-package-count`,
        {
          params: this.dashboardUrlService.stateToHttpParams(
            filters.filters as FilterDescriptor[],
          ),
        },
      );
    } else {
      return this.httpClient.get<{ count: number }>(
        `${this.apiUrl}/hunt-metrics/hunt-package-count`,
      );
    }
  }

  public getUniqueDashboardHuntPackageCount(
    filters?: CompositeFilterDescriptor,
  ): Observable<{ count: number }> {
    if (filters && filters.filters) {
      return this.httpClient.get<{ count: number }>(
        `${this.apiUrl}/hunt-metrics/hunt-package-unique-count`,
        {
          params: this.dashboardUrlService.stateToHttpParams(
            filters.filters as FilterDescriptor[],
          ),
        },
      );
    } else {
      return this.httpClient.get<{ count: number }>(
        `${this.apiUrl}/hunt-metrics/hunt-package-unique-count`,
      );
    }
  }

  public getDashboardHuntCount(
    filters?: CompositeFilterDescriptor,
  ): Observable<{ count: number }> {
    if (filters && filters.filters) {
      return this.httpClient.get<{ count: number }>(
        `${this.apiUrl}/hunt-metrics/hunt-count`,
        {
          params: this.dashboardUrlService.stateToHttpParams(
            filters.filters as FilterDescriptor[],
          ),
        },
      );
    } else {
      return this.httpClient.get<{ count: number }>(
        `${this.apiUrl}/hunt-metrics/hunt-count`,
      );
    }
  }
}
